import { Injectable, OnDestroy } from '@angular/core';
import { combineQueries } from '@datorama/akita';
import { MappingRulesQuery } from '../../../../features/accounting/mapping-rules/state/mapping-rules.query';
import { TransactionsQuery } from '../../../../features/transactions/state/transactions.query';
import { Observable, Subject } from 'rxjs';
import { debounceTime, switchMap, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { AppConfigQuery } from '../../../../core/state/app-config/app-config.query';
import { Book } from '../../../../features/accounting/books/state/book.model';
import { Instrument } from '../../../../shared/state/instruments/instrument.model';
import { Transaction } from '../../../../features/transactions/state/transaction.model';
import { BooksQuery } from '../../../../features/accounting/books/state/books.query';

@Injectable({
    providedIn: 'root'
})
export class SyncAccountingService implements OnDestroy {
    unsubscribe$ = new Subject();
    constructor(
        private appConfig: AppConfigQuery,
        private _rulesQuery: MappingRulesQuery,
        private _booksQuery: BooksQuery,
        private _transactionsQuery: TransactionsQuery,
        private _journalEntriesQuery: TransactionsQuery,
        private http: HttpClient
    ) {
        combineQueries([
            this._transactionsQuery.selectEntityAction(),
            this._journalEntriesQuery.selectEntityAction(),
            this._rulesQuery.selectEntityAction()
        ])
            // TODO: pass affected transaction group id to trigger calculation
            .pipe(
                debounceTime(1000),
                switchMap(([transactionAction, journalEntitiesAction, rulesAction]) => {
                    return this.recalculate();
                }),
                takeUntil(this.unsubscribe$)
            )
            .subscribe((_) => {
                console.log('triggered calculation');
            });
    }

    recalculate(
        params: {
            bookIds?: Array<Book['id']>;
            instrumentId?: Instrument['instrumentId'];
            transactionGroupIds?: Array<Transaction['transactionGroupId']>;
            fromDate?: string;
            toDate?: string;
            forceRecalculate?: boolean;
        } = {}
    ) {
        return this._booksQuery.activeBook$.pipe(
            switchMap((book) => {
                return this.http.put(
                    `${
                        this.appConfig.get().accountingBaseUrl
                    }/accounting/v1/calculations/trigger`,
                    null,
                    {
                        params: { ...params, bookIds: params.bookIds || book.id }
                    }
                );
            })
        );
    }

    /**
     *
     * @returns Observable of start calculation
     */
    recalculateAsync(
        params: {
            bookIds?: Array<Book['id']>;
            instrumentId?: Instrument['instrumentId'];
            transactionGroupIds?: Array<Transaction['transactionGroupId']>;
            fromDate?: string;
            toDate?: string;
            forceRecalculate?: boolean;
        } = {}
    ) {
        return this._booksQuery.activeBook$.pipe(
            switchMap((book) => {
                return this.http.put(
                    `${
                        this.appConfig.get().accountingBaseUrl
                    }/accounting/v1/calculations/trigger-async`,
                    null,
                    {
                        params: { ...params, bookIds: params.bookIds || book.id }
                    }
                );
            })
        );
    }

    /**
     *
     * @returns Observable of calculation status
     */
    fetchCalculationStatus(): Observable<{ status: boolean; duration?: number }> {
        return this.http.get<{ status: boolean; duration?: number }>(
            `${this.appConfig.get().accountingBaseUrl}/accounting/v1/calculations/status`
        );
    }

    ngOnDestroy() {
        console.log('destroying sync accounting service');
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }
}
